import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, Upload, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CollapsibleSection } from '@/components/ui/collapsible-section'
import { fetchWithAuth } from '@/lib/api'
import {
  type ChatFeedbackCategory,
  type ChatFeedbackOpenDetail,
  buildPilotFeedbackFolderLabel,
  formatPilotFeedbackPathForDisplay,
  PILOT_FEEDBACK_LANDING_DISPLAY_LABEL,
} from '@/lib/chat-feedback'
import { getFeedbackUiContext } from '@/lib/feedback-ui-context'
import { storagePathToUrlPath } from '@/lib/app-routes'
import { useWorkspace } from '@/providers/workspace-provider'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

export type FeedbackMessageSnapshot = {
  role: string
  content: string
  createdAt?: string
}

type StagedScreenshot = {
  id: string
  file: File
  previewUrl: string
}

const CATEGORY_OPTIONS: { value: ChatFeedbackCategory; label: string }[] = [
  { value: 'feedback', label: 'Feedback' },
  { value: 'bug', label: 'Bug' },
  { value: 'agent-issue', label: 'Agent issue' },
  { value: 'question', label: 'Question' },
]

const RATING_LABELS = ['1', '2', '3', '4', '5']

const MAX_SCREENSHOTS = 5
const MAX_SCREENSHOT_BYTES = 10 * 1024 * 1024
const ALLOWED_IMAGE_PREFIX = 'image/'

const formSchema = z.object({
  category: z.enum(['bug', 'feedback', 'agent-issue', 'question']),
  description: z.string().min(1, 'Description is required'),
  expected: z.string().optional(),
  actual: z.string().optional(),
  rating: z.number().int().min(1).max(5).optional().nullable(),
})

type FormValues = z.infer<typeof formSchema>

export type FeedbackModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialDetail?: ChatFeedbackOpenDetail
  messages?: Array<{ role: string; content: string }>
  conversationTitle?: string | null
  workspaceScope?: string[]
  onSuccess?: (result: { documentId: string; filePath: string }) => void
  prototypeScene?:
    | 'idle'
    | 'filled'
    | 'filing'
    | 'success'
    | 'error'
    | 'mobile'
    | 'screenshots'
    | null
  prototypeSuccessPath?: string
  prototypeErrorMessage?: string
  /** Prototype-only staged screenshot previews (object URLs or data URLs). */
  prototypeStagedScreenshots?: Array<{ id: string; previewUrl: string; name?: string }>
}

/** Pre-fill description from conversation title or the last user message line. */
function prefillDescription(
  conversationTitle?: string | null,
  messages?: Array<{ role: string; content: string }>,
): string {
  if (conversationTitle?.trim()) return conversationTitle.trim()
  const lastUser = [...(messages || [])].reverse().find((m) => m.role === 'user')
  if (lastUser?.content?.trim()) {
    const line = lastUser.content.trim().split('\n')[0]
    return line.length > 120 ? `${line.slice(0, 117)}...` : line
  }
  return ''
}

/** Take the last N chat messages and truncate long content for the API payload. */
function buildMessageSnapshot(
  messages?: Array<{ role: string; content: string }>,
  max = 10,
  maxChars = 4000,
): FeedbackMessageSnapshot[] {
  if (!messages?.length) return []
  const slice = messages.slice(-max)
  return slice.map((m) => {
    const content = m.content || ''
    const truncated =
      content.length > maxChars ? `${content.slice(0, maxChars)}\n...(truncated)` : content
    return { role: m.role, content: truncated }
  })
}

/** Revoke object URLs for staged screenshot previews to avoid leaks. */
function revokePreviewUrls(shots: StagedScreenshot[]) {
  for (const shot of shots) {
    URL.revokeObjectURL(shot.previewUrl)
  }
}

/** Structured pilot feedback dialog; submits to POST /api/pilot-feedback. */
export function FeedbackModal({
  open,
  onOpenChange,
  initialDetail,
  messages,
  conversationTitle,
  workspaceScope,
  onSuccess,
  /** Prototype-only forced UI state (?test=feedback-modal) */
  prototypeScene,
  prototypeSuccessPath,
  prototypeErrorMessage,
  prototypeStagedScreenshots,
}: FeedbackModalProps) {
  const defaultCategory = initialDetail?.category ?? 'feedback'

  // Landing is always FlowNotes Product (server-side). Label is user-facing only - never ws/{hex}.
  const { activeWorkspaces, workspaces } = useWorkspace()
  const activeWorkspace = activeWorkspaces[0] ?? null
  const targetFolderLabel = buildPilotFeedbackFolderLabel(activeWorkspace?.contentRoot)

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    expectedActual: defaultCategory === 'bug',
  })
  const [submitting, setSubmitting] = useState(false)
  const [successResult, setSuccessResult] = useState<{
    documentId: string
    filePath: string
    screenshotErrorCount?: number
  } | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [stagedScreenshots, setStagedScreenshots] = useState<StagedScreenshot[]>([])
  const [screenshotError, setScreenshotError] = useState<string | null>(null)
  const [isDraggingScreenshots, setIsDraggingScreenshots] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const stagedScreenshotsRef = useRef<StagedScreenshot[]>([])

  const isPrototypeFiling = prototypeScene === 'filing'
  const isPrototypeSuccess = prototypeScene === 'success'
  const isPrototypeError = prototypeScene === 'error'
  const isMobilePrototype = prototypeScene === 'mobile'
  const isPrototypeScreenshots = prototypeScene === 'screenshots'

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      category: defaultCategory,
      description: '',
      expected: '',
      actual: '',
      rating: null,
    },
  })

  const clearStagedScreenshots = useCallback(() => {
    revokePreviewUrls(stagedScreenshotsRef.current)
    stagedScreenshotsRef.current = []
    setStagedScreenshots([])
    setScreenshotError(null)
  }, [])

  const resetFormFromContext = useCallback(() => {
    const description =
      prototypeScene === 'filled' || prototypeScene === 'screenshots'
        ? 'Chat agent narrated tool calls instead of showing progress cards during Basecamp ingest.'
        : prefillDescription(conversationTitle, messages)
    form.reset({
      category: initialDetail?.category ?? defaultCategory,
      description,
      expected:
        prototypeScene === 'filled' || prototypeScene === 'screenshots'
          ? 'Silent tool progress in the chat stream'
          : '',
      actual:
        prototypeScene === 'filled' || prototypeScene === 'screenshots'
          ? 'Long prose narration with no structured cards'
          : '',
      rating: prototypeScene === 'filled' || prototypeScene === 'screenshots' ? 2 : null,
    })
    setSuccessResult(null)
    setSubmitError(null)
    setSubmitting(false)
    clearStagedScreenshots()
  }, [
    clearStagedScreenshots,
    conversationTitle,
    defaultCategory,
    form,
    initialDetail?.category,
    messages,
    prototypeScene,
  ])

  // Seed the form exactly once per modal-open session. `resetFormFromContext` depends on
  // `messages`/`conversationTitle`, which are live streaming state from the parent chat pane -
  // if a background conversation is still streaming while this modal is open, those props get
  // a new value on every SSE chunk, changing this callback's identity and (pre-fix) re-firing
  // `form.reset()` on every chunk, silently wiping out anything the user had already typed in
  // the description textarea. See docs/product/incidents/2026-07-09-feedback-widget-description-reset-during-streaming.md.
  const hasSeededRef = useRef(false)
  useEffect(() => {
    if (open) {
      if (!hasSeededRef.current) {
        hasSeededRef.current = true
        resetFormFromContext()
      }
    } else {
      hasSeededRef.current = false
      clearStagedScreenshots()
    }
  }, [open, resetFormFromContext, clearStagedScreenshots])

  useEffect(() => {
    return () => {
      revokePreviewUrls(stagedScreenshotsRef.current)
    }
  }, [])

  const sessionContext = useMemo(
    () => ({
      conversationId: initialDetail?.conversationId ?? null,
      threadId: initialDetail?.threadId ?? null,
      model: initialDetail?.model ?? null,
      provider: initialDetail?.provider ?? null,
    }),
    [initialDetail],
  )

  const modelDisplay = sessionContext.model
    ? sessionContext.provider
      ? `${sessionContext.provider}/${sessionContext.model}`
      : sessionContext.model
    : 'unknown'

  const watchedCategory = form.watch('category')
  useEffect(() => {
    if (watchedCategory === 'bug') {
      setExpandedSections((prev) => ({ ...prev, expectedActual: true }))
    }
  }, [watchedCategory])

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const handleClose = (next: boolean) => {
    if (!next) {
      clearStagedScreenshots()
      onOpenChange(false)
    }
  }

  const stageImageFiles = useCallback((incoming: File[]) => {
    if (!incoming.length) return

    setScreenshotError(null)
    const current = stagedScreenshotsRef.current
    const remaining = MAX_SCREENSHOTS - current.length
    if (remaining <= 0) {
      setScreenshotError(`Maximum ${MAX_SCREENSHOTS} screenshots allowed`)
      return
    }

    const accepted: StagedScreenshot[] = []
    let error: string | null = null

    for (const file of incoming) {
      if (accepted.length >= remaining) {
        error = `Maximum ${MAX_SCREENSHOTS} screenshots allowed`
        break
      }
      if (!file.type.startsWith(ALLOWED_IMAGE_PREFIX)) {
        error = `Only image files are allowed (got ${file.type || file.name})`
        continue
      }
      if (file.size === 0) {
        error = `"${file.name}" is empty`
        continue
      }
      if (file.size > MAX_SCREENSHOT_BYTES) {
        error = `"${file.name}" exceeds 10MB limit`
        continue
      }
      accepted.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        file,
        previewUrl: URL.createObjectURL(file),
      })
    }

    if (accepted.length > 0) {
      const next = [...current, ...accepted]
      stagedScreenshotsRef.current = next
      setStagedScreenshots(next)
    }
    if (error) setScreenshotError(error)
  }, [])

  const removeStagedScreenshot = useCallback((id: string) => {
    const current = stagedScreenshotsRef.current
    const target = current.find((s) => s.id === id)
    if (target) URL.revokeObjectURL(target.previewUrl)
    const next = current.filter((s) => s.id !== id)
    stagedScreenshotsRef.current = next
    setStagedScreenshots(next)
    setScreenshotError(null)
  }, [])

  const handlePasteScreenshots = useCallback(
    (e: React.ClipboardEvent) => {
      const items = Array.from(e.clipboardData?.items || [])
      // Prefer a single image: OS clipboards often expose the same capture as
      // image/png + image/tiff (or similar). Taking every image/* item duplicates.
      const imageItem =
        items.find((item) => item.type === 'image/png') ||
        items.find((item) => item.type.startsWith(ALLOWED_IMAGE_PREFIX))
      if (!imageItem) return

      const file = imageItem.getAsFile()
      if (!file || file.size === 0) return

      e.preventDefault()
      e.stopPropagation()
      stageImageFiles([file])
    },
    [stageImageFiles],
  )

  const handleDropScreenshots = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDraggingScreenshots(false)
      const files = Array.from(e.dataTransfer.files).filter((f) =>
        f.type.startsWith(ALLOWED_IMAGE_PREFIX),
      )
      if (files.length > 0) {
        stageImageFiles(files)
      } else if (e.dataTransfer.files.length > 0) {
        setScreenshotError('Only image files are allowed')
      }
    },
    [stageImageFiles],
  )

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || [])
      if (files.length > 0) stageImageFiles(files)
      if (fileInputRef.current) fileInputRef.current.value = ''
    },
    [stageImageFiles],
  )

  const onSubmit = async (values: FormValues) => {
    setSubmitting(true)
    setSubmitError(null)
    try {
      const ui = getFeedbackUiContext()
      const payload = {
        category: values.category,
        description: values.description,
        expected: values.expected?.trim() || undefined,
        actual: values.actual?.trim() || undefined,
        rating: values.rating ?? undefined,
        conversationId: sessionContext.conversationId ?? undefined,
        threadId: sessionContext.threadId ?? undefined,
        model: sessionContext.model ?? undefined,
        provider: sessionContext.provider ?? undefined,
        messageSnapshot: buildMessageSnapshot(messages),
        workspaceScope: workspaceScope ?? undefined,
        organizationId: activeWorkspace?.id ?? undefined,
        pageUrl: ui.pageUrl ?? undefined,
        pagePath: ui.pagePath ?? undefined,
        activeTab: ui.activeTabId
          ? {
              id: ui.activeTabId,
              title: ui.activeTabTitle,
              type: ui.activeTabType,
              filePath: ui.activeDocumentPath,
            }
          : undefined,
        openTabs: ui.openTabs.length
          ? ui.openTabs.map((t) => ({
              title: t.title,
              type: t.type,
              filePath: t.filePath ?? undefined,
            }))
          : undefined,
      }

      let res: Response
      if (stagedScreenshots.length > 0) {
        const formData = new FormData()
        formData.append('payload', JSON.stringify(payload))
        for (const shot of stagedScreenshots) {
          formData.append('screenshots', shot.file, shot.file.name)
        }
        res = await fetchWithAuth('/api/pilot-feedback', {
          method: 'POST',
          body: formData,
        })
      } else {
        res = await fetchWithAuth('/api/pilot-feedback', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })
      }

      const data = await res.json().catch(() => ({}))

      if (!res.ok || !data.success) {
        throw new Error(data.error || `Request failed (${res.status})`)
      }

      const screenshotErrorCount = Array.isArray(data.screenshotErrors)
        ? data.screenshotErrors.length
        : 0

      clearStagedScreenshots()
      setSuccessResult({
        documentId: String(data.documentId),
        filePath: String(data.filePath),
        screenshotErrorCount: screenshotErrorCount > 0 ? screenshotErrorCount : undefined,
      })
      onSuccess?.({
        documentId: String(data.documentId),
        filePath: String(data.filePath),
      })
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit feedback')
    } finally {
      setSubmitting(false)
    }
  }

  const showForm =
    !successResult &&
    !submitError &&
    !isPrototypeSuccess &&
    !isPrototypeError &&
    !isPrototypeFiling

  const showSuccess = successResult || isPrototypeSuccess
  const showError = (submitError && !submitting) || isPrototypeError
  const showFiling = submitting || isPrototypeFiling

  const successPath =
    successResult?.filePath ??
    prototypeSuccessPath ??
    `${PILOT_FEEDBACK_LANDING_DISPLAY_LABEL}/2026-06-20-sample-feedback.md`

  const successPathDisplay = formatPilotFeedbackPathForDisplay(
    successPath,
    storagePathToUrlPath(
      successPath,
      workspaces.map((w) => ({ id: w.id, slug: w.slug, contentRoot: w.contentRoot })),
    ),
  )

  const errorMessage =
    submitError ?? prototypeErrorMessage ?? 'Network error. Could not reach the API.'

  const displayThumbnails: Array<{ id: string; previewUrl: string; name?: string }> =
    isPrototypeScreenshots && prototypeStagedScreenshots?.length
      ? prototypeStagedScreenshots
      : stagedScreenshots.map((s) => ({
          id: s.id,
          previewUrl: s.previewUrl,
          name: s.file.name,
        }))

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        data-testid={testIds.feedbackModal.root}
        className={cn(
          'max-h-[90vh] overflow-y-auto',
          isMobilePrototype ? 'max-w-sm w-[calc(100%-2rem)]' : 'max-w-lg',
        )}
        onPaste={handlePasteScreenshots}
      >
        <DialogHeader>
          <DialogTitle>File pilot feedback</DialogTitle>
        </DialogHeader>

        <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Target folder: </span>
          <span data-testid={testIds.feedbackModal.targetFolder}>{targetFolderLabel}</span>
        </div>

        <div
          className="rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-xs space-y-1"
          data-testid={testIds.feedbackModal.sessionContext}
        >
          <p>
            <span className="font-medium text-foreground">Conversation: </span>
            {sessionContext.conversationId ?? 'none'}
          </p>
          <p>
            <span className="font-medium text-foreground">Thread: </span>
            {sessionContext.threadId ?? 'none'}
          </p>
          <p>
            <span className="font-medium text-foreground">Model: </span>
            {modelDisplay}
          </p>
        </div>

        {showFiling && (
          <div
            className="flex flex-col items-center justify-center gap-3 py-8 text-sm text-muted-foreground"
            data-testid={testIds.feedbackModal.filing}
          >
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p>Filing feedback...</p>
          </div>
        )}

        {showSuccess && !showFiling && (
          <div className="space-y-4 py-4" data-testid={testIds.feedbackModal.success}>
            <p className="text-sm text-foreground">Feedback filed successfully.</p>
            {successResult?.screenshotErrorCount ? (
              <p className="text-sm text-amber-600 dark:text-amber-400">
                Filed, but {successResult.screenshotErrorCount} screenshot(s) failed to upload
              </p>
            ) : null}
            <p className="text-xs text-muted-foreground break-all">{successPathDisplay}</p>
            <DialogFooter className="sm:justify-start">
              <Button type="button" onClick={() => onOpenChange(false)}>Close</Button>
            </DialogFooter>
          </div>
        )}

        {showError && !showFiling && !showSuccess && (
          <div className="space-y-4 py-4" data-testid={testIds.feedbackModal.error}>
            <p className="text-sm text-destructive">{errorMessage}</p>
            <DialogFooter className="sm:justify-start gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSubmitError(null)
                  if (!isPrototypeError) onSubmit(form.getValues())
                }}
                data-testid={testIds.feedbackModal.retry}
              >
                Retry
              </Button>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </DialogFooter>
          </div>
        )}

        {showForm && !showFiling && (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger data-testid={testIds.feedbackModal.category}>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CATEGORY_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={4}
                        placeholder="What happened?"
                        data-testid={testIds.feedbackModal.description}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <p className="text-sm font-medium leading-none">Screenshots (optional)</p>
                <div
                  role="button"
                  tabIndex={0}
                  data-testid={testIds.feedbackModal.screenshotDropzone}
                  className={cn(
                    'border-2 border-dashed rounded-lg px-3 py-4 text-center transition-colors cursor-pointer',
                    isDraggingScreenshots
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50',
                  )}
                  onDragEnter={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setIsDraggingScreenshots(true)
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setIsDraggingScreenshots(false)
                  }}
                  onDragOver={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                  onDrop={handleDropScreenshots}
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      fileInputRef.current?.click()
                    }
                  }}
                >
                  <Upload className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {isDraggingScreenshots
                      ? 'Drop screenshots here'
                      : 'Drag screenshots here or click to attach'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Paste, drop, or pick images (max {MAX_SCREENSHOTS}, 10MB each)
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    data-testid={testIds.feedbackModal.screenshotFileInput}
                    onChange={handleFileInputChange}
                  />
                </div>

                {screenshotError ? (
                  <p className="text-sm font-medium text-destructive">{screenshotError}</p>
                ) : null}

                {displayThumbnails.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {displayThumbnails.map((shot) => (
                      <div
                        key={shot.id}
                        className="relative group"
                        data-testid={testIds.feedbackModal.screenshotThumbnail}
                      >
                        <img
                          src={shot.previewUrl}
                          alt={shot.name || 'Screenshot preview'}
                          className="h-16 w-16 object-cover rounded-md border border-border"
                        />
                        {!isPrototypeScreenshots && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              removeStagedScreenshot(shot.id)
                            }}
                            className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-foreground text-background flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label="Remove screenshot"
                            data-testid={testIds.feedbackModal.screenshotRemoveButton}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <CollapsibleSection
                id="expectedActual"
                title="Expected / Actual (optional)"
                isExpanded={expandedSections.expectedActual ?? false}
                onToggle={toggleSection}
                variant="inline"
              >
                <div className="space-y-3">
                  <FormField
                    control={form.control}
                    name="expected"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">Expected</FormLabel>
                        <FormControl>
                          <Textarea {...field} rows={2} data-testid={testIds.feedbackModal.expected} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="actual"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-xs">Actual</FormLabel>
                        <FormControl>
                          <Textarea {...field} rows={2} data-testid={testIds.feedbackModal.actual} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </CollapsibleSection>

              <FormField
                control={form.control}
                name="rating"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rating (optional)</FormLabel>
                    <div className="flex gap-1" data-testid={testIds.feedbackModal.rating}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Button
                          key={n}
                          type="button"
                          variant={field.value === n ? 'default' : 'outline'}
                          size="sm"
                          className="min-w-9 px-2"
                          onClick={() =>
                            field.onChange(field.value === n ? null : n)
                          }
                          aria-label={`Rating ${n}`}
                        >
                          {RATING_LABELS[n - 1]}
                        </Button>
                      ))}
                    </div>
                  </FormItem>
                )}
              />

              {sessionContext.conversationId && (
                <p className="text-xs text-muted-foreground">
                  Submitting shares this conversation with the Maater team so they can see what
                  happened.
                </p>
              )}

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid={testIds.feedbackModal.submit}
                >
                  Submit feedback
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}
